<template>
    <div class="grid-container md:p-4">
        <PlanLockOverlay v-for="(tutor, index) in tutors" :key="index" :locked="!!tutor.lockedByPlan"
            message="Locked — this tutor is beyond your plan's tutor limit. Classes are paused and will not be charged until you upgrade or end another enrolment.">
            <Card class="Card p-4 h-full">
                <template #content>
                    <div class="flex flex-column align-item-start justify-content-start gap-2 text-sm">
                        <div class="flex justify-content-start gap-2 ">
                            <Avatar
                                :image="!tutor.tutorPhotoURL || tutor.tutorPhotoURL === 'Service is not available in local environment' ? blankProfile : profileImageUrl(tutor.tutorPhotoURL)"
                                alt="Image" class="mr-2 flex-shrink-0" size="xlarge" shape="circle" />
                            <div class="flex flex-column justify-content-center gap-2 pb-2 ">
                                <span class="text-left Paragraph-Medium -mb-2 tutor-name">{{ tutor.tutorName }}</span>
                                <span class="Caption"> {{ cleanupLables(tutor.tutorType) }}</span>
                            </div>
                        </div>
                        <div class="flex justify-content-start gap-2">
                            <span class="Paragraph-Regular">{{ tutor.classModes.map(m => capitalize(m)).join(' | ') }}</span>
                        </div>
                        <div class="flex justify-content-start gap-2 pb-2 ">
                            <span class="Paragraph-Regular">{{ formatClassModels(tutor.classModels) }}</span>
                        </div>
                        <div class="flex flex-wrap align-items-center justify-content-start gap-2 ">
                            <span v-for="subject in tutor.subjects" :key="subject"
                                class="flex items-center subjects justify-center px-2 py-2 Caption">
                                {{cleanupLables(subject) }}</span>
                        </div>
                    </div>
                </template>
                <template #footer>
                    <div class="flex justify-content-start gap-2 pt-2">
                        <span class="Caption">Next class date: {{ tutor.nextClassDate }}</span>
                    </div>
                    <div class="flex justify-content-center align-items-center gap-2 mt-3">
                        <div style="width: 30px;"></div>
                        <Button label="View" class="orange-button-small" @click="$emit('view-tutor', tutor)" />
                        <ChatButton :otherUserId="tutor.tutorId" :otherUserName="tutor.tutorName" @open-chat="$emit('open-chat', $event)" />
                    </div>
                </template>
            </Card>
        </PlanLockOverlay>
    </div>
</template>

<script setup>
import { defineProps, defineEmits, ref } from "vue";
import Card from "primevue/card";
import Button from "primevue/button";
import PlanLockOverlay from "@/components/PlanLockOverlay.vue";
import blankProfile from '@/assets/blank-profile.png';
import { generateSignedUrl } from '@/api/storage';
import Avatar from "primevue/avatar";
import { capitalize, cleanupLables } from "@/utils/cleanups";
import ChatButton from "@/components/ChatButton.vue";

defineProps({
    tutors: {
        type: Array,
        required: true
    }
});

defineEmits(['view-tutor', 'open-chat']);

const imageUrls = ref({});
const profileImageUrl = (filePath) => {
    if (imageUrls.value[filePath]) return imageUrls.value[filePath];

    generateSignedUrl(filePath)
        .then(url => {
            imageUrls.value[filePath] = url;
        })
        .catch(() => {
            imageUrls.value[filePath] = blankProfile;
        });
    return imageUrls.value[filePath];
};

const formatClassModels = (classModels) => {
    if (!classModels || !classModels.length) return "Class Models Unavailable";

    const individual = classModels.includes('individual_class');
    const group = classModels.includes('group_class');

    if (individual && group) return "Individual & Group Classes";
    if (individual) return "Individual Classes";
    if (group) return "Group Classes";

    return "Class Models Unavailable";
};
</script>

<style scoped>
.grid-container {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(16rem, 1fr));
    gap: 3rem;
}

.Card {
    border-radius: 4px;
    box-shadow: 0 0 4px 1px rgba(0, 0, 0, 0.16);
    height: 100%;
}

.Card :deep(.p-card-body) {
    display: flex;
    flex-direction: column;
    height: 100%;
}

.Card :deep(.p-card-content) {
    flex: 1;
}

.Card :deep(.p-card-footer) {
    margin-top: auto;
}

.teal-button {
    padding: 8px 16px 8px 16px;
    height: 30px;
    font-size: 0.75rem;

}

.p-button-danger {
    padding: 10px 16px 10px 16px;
    height: 30px;
}

.text-gray-700 {
    color: var(--gray-700) !important;
    padding-top: 30vh;
}

.subjects {
    border: 1px solid var(--gray-700) !important;
    border-radius: 50px;
    background-color: transparent;
}

.tutor-name {
    overflow-wrap: anywhere;
    word-break: break-word;
}

.flex-shrink-0 {
    flex-shrink: 0;
}

.Card :deep(.p-avatar.p-avatar-xl) {
    width: 4rem;
    height: 4rem;
    flex-shrink: 0;
}

.Card :deep(.p-avatar.p-avatar-xl img) {
    width: 100%;
    height: 100%;
    object-fit: cover;
}
</style>